import { useEffect, useReducer } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { DateField } from "@/components/availability/DateField";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { startAdminConversation } from "@/lib/messaging";
import { toIso } from "@/lib/availability";
import { track } from "@/lib/track";
import {
  deleteListing,
  isPublished,
  keepPublishedFrom,
  markRented,
  pauseListing,
  recordDelistEvent,
  updateDelistEvent,
} from "@/lib/listingLifecycle";
import {
  DELIST_REASONS,
  FOUND_VIA_OPTIONS,
  reasonsForIntent,
  recommendedAlternative,
  type DelistAction,
  type DelistIntent,
  type DelistReason,
  type FoundVia,
} from "@/lib/delistReasons";

// The one dialog for taking a listing off the market. Reason first (six
// options), then the single alternative that fits that reason, then, only if
// the owner insists, the typed "delete" confirmation. Every step is recorded
// so the team sees why owners leave and how many were kept.

export interface DelistListing {
  id: string;
  title: string | null;
  status: string;
  /** The listing's owner (the event is recorded against them, even when an admin acts). */
  owner_id?: string | null;
  tenant_id?: string | null;
  photos?: unknown;
  image_urls?: string[] | null;
  video_urls?: string[] | null;
}

export interface DelistOutcome {
  action: DelistAction;
  reason: DelistReason;
  availableFrom?: string | null;
  reminderDate?: string | null;
  reviewRequested?: boolean;
}

interface Candidate {
  id: string;
  name: string;
}

type Step = "reason" | "offer" | "confirm_delete" | "done";

interface State {
  step: Step;
  reason: DelistReason | null;
  foundVia: FoundVia | null;
  matchedRequestId: string | null;
  reasonText: string;
  availableAgain: Date | undefined;
  reminderDate: Date | undefined;
  typed: string;
  submitting: boolean;
  error: string | null;
  eventId: string | null;
  pendingCount: number;
  candidates: Candidate[];
  outcome: DelistOutcome | null;
}

type Action =
  | { type: "RESET" }
  | { type: "LOADED"; pendingCount: number; candidates: Candidate[] }
  | { type: "SET"; patch: Partial<State> }
  | { type: "GO"; step: Step }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_ERROR"; error: string }
  | { type: "DONE"; outcome: DelistOutcome };

const initial: State = {
  step: "reason",
  reason: null,
  foundVia: null,
  matchedRequestId: null,
  reasonText: "",
  availableAgain: undefined,
  reminderDate: undefined,
  typed: "",
  submitting: false,
  error: null,
  eventId: null,
  pendingCount: 0,
  candidates: [],
  outcome: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "RESET":
      return initial;
    case "LOADED":
      return { ...state, pendingCount: action.pendingCount, candidates: action.candidates };
    case "SET":
      return { ...state, ...action.patch };
    case "GO":
      return { ...state, step: action.step, error: null, submitting: false };
    case "SUBMIT_START":
      return { ...state, submitting: true, error: null };
    case "SUBMIT_ERROR":
      return { ...state, submitting: false, error: action.error };
    case "DONE":
      return { ...state, submitting: false, step: "done", outcome: action.outcome };
    default:
      return state;
  }
};

const CONFIRM_WORD = "delete";
const fmt = (d: Date) => format(d, "MMM d, yyyy");

interface DelistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: DelistListing | null;
  intent: DelistIntent;
  /** Fired after the dialog auto-closes with what happened. */
  onDone?: (outcome: DelistOutcome) => void;
}

const DelistDialog = ({ open, onOpenChange, listing, intent, onDone }: DelistDialogProps) => {
  const [state, dispatch] = useReducer(reducer, initial);
  const { user } = useAuth();
  const tenant = useTenant();
  const listingId = listing?.id ?? null;

  useEffect(() => {
    if (!open) dispatch({ type: "RESET" });
  }, [open]);

  // Pending requests (named in the delete confirmation) and accepted renters
  // (the optional "which renter?" pick when the tenant was found here).
  useEffect(() => {
    if (!open || !listingId) return;
    let cancelled = false;
    (async () => {
      const [{ count }, { data: accepted }] = await Promise.all([
        supabase
          .from("contact_requests")
          .select("id", { count: "exact", head: true })
          .eq("property_id", listingId)
          .eq("status", "pending")
          .eq("direction", "renter_to_owner"),
        supabase.from("contact_requests").select("id, renter_id").eq("property_id", listingId).eq("status", "accepted"),
      ]);
      let candidates: Candidate[] = [];
      if (accepted?.length) {
        const ids = [...new Set(accepted.map((a) => a.renter_id))];
        const { data: profs } = await supabase.from("profiles").select("user_id, display_name").in("user_id", ids);
        candidates = accepted.map((a) => ({
          id: a.id,
          name: profs?.find((p) => p.user_id === a.renter_id)?.display_name?.trim() || "A renter",
        }));
      }
      if (!cancelled) dispatch({ type: "LOADED", pendingCount: count ?? 0, candidates });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listingId]);

  // Confirmation step: short pause, then close and let the parent refresh.
  useEffect(() => {
    if (state.step !== "done" || !state.outcome) return;
    const outcome = state.outcome;
    const t = setTimeout(() => {
      onOpenChange(false);
      onDone?.(outcome);
    }, 1400);
    return () => clearTimeout(t);
  }, [state.step, state.outcome, onOpenChange, onDone]);

  if (!listing) return null;
  const title = listing.title?.trim() || "this listing";
  const reasons = reasonsForIntent(intent);

  const finish = async (outcome: DelistOutcome, eventPatch: Parameters<typeof updateDelistEvent>[1]) => {
    await updateDelistEvent(state.eventId, eventPatch);
    track("listing_delisted", { property_id: listing.id, reason: outcome.reason, action: outcome.action, intent });
    dispatch({ type: "DONE", outcome });
  };

  const fail = (e: unknown) =>
    dispatch({ type: "SUBMIT_ERROR", error: e instanceof Error ? e.message : "Something went wrong. Please try again." });

  const handlePickReason = async () => {
    if (!state.reason || !user) return;
    dispatch({ type: "SUBMIT_START" });
    const eventId = await recordDelistEvent({
      property_id: listing.id,
      property_title: listing.title,
      owner_id: listing.owner_id ?? user.id,
      tenant_id: listing.tenant_id ?? tenant.id,
      reason_code: state.reason,
      reason_text: state.reasonText.trim() || null,
      found_via: state.reason === "found_elsewhere" ? state.foundVia : null,
      matched_request_id: state.reason === "found_here" ? state.matchedRequestId : null,
    });
    dispatch({ type: "SET", patch: { eventId } });
    dispatch({ type: "GO", step: recommendedAlternative(state.reason) === "delete" ? "confirm_delete" : "offer" });
  };

  const handleKeepPublished = async () => {
    if (!state.reason || !state.availableAgain) return;
    dispatch({ type: "SUBMIT_START" });
    const iso = toIso(state.availableAgain);
    const { error } = await keepPublishedFrom(listing.id, iso, listing.status);
    if (error) return fail(error);
    await finish(
      { action: "kept_published", reason: state.reason, availableFrom: iso },
      { action: "kept_published", recovered: true, step_reached: 2, available_again: iso },
    );
  };

  const handleRented = async () => {
    if (!state.reason) return;
    dispatch({ type: "SUBMIT_START" });
    const { error } = await markRented(listing.id);
    if (error) return fail(error);
    await finish({ action: "rented", reason: state.reason }, { action: "rented", step_reached: 2 });
  };

  const handlePause = async () => {
    if (!state.reason) return;
    dispatch({ type: "SUBMIT_START" });
    const reminder = state.reminderDate ? toIso(state.reminderDate) : null;
    const { error } = await pauseListing(listing.id, reminder);
    if (error) return fail(error);
    await finish(
      { action: "paused", reason: state.reason, reminderDate: reminder },
      { action: "paused", step_reached: 2, relist_reminder_at: reminder },
    );
  };

  const handleReview = async () => {
    if (!state.reason) return;
    dispatch({ type: "SUBMIT_START" });
    try {
      await startAdminConversation(
        `Listing review: ${title}`,
        `Hi team, I'm getting few requests for "${title}" and would like a quick review of the listing before taking it down.${
          state.reasonText.trim() ? `\n\n${state.reasonText.trim()}` : ""
        }`,
        listing.tenant_id ?? tenant.id,
      );
    } catch (e) {
      return fail(e);
    }
    await finish(
      { action: "kept_published", reason: state.reason, reviewRequested: true },
      { action: "kept_published", recovered: true, step_reached: 2 },
    );
  };

  const canDelete = state.typed.trim().toLowerCase() === CONFIRM_WORD && !state.submitting;

  const handleDelete = async () => {
    if (!canDelete || !state.reason) return;
    dispatch({ type: "SUBMIT_START" });
    // Record the outcome while the listing still exists (the row keeps its
    // title after the FK is cleared), then delete.
    await updateDelistEvent(state.eventId, { action: "deleted", step_reached: 3 });
    const { error } = await deleteListing(listing);
    if (error) {
      await updateDelistEvent(state.eventId, { action: "undecided", step_reached: 3 });
      return fail(error);
    }
    track("listing_delisted", { property_id: listing.id, reason: state.reason, action: "deleted", intent });
    dispatch({ type: "DONE", outcome: { action: "deleted", reason: state.reason } });
  };

  const alternative = state.reason ? recommendedAlternative(state.reason) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const renderReason = () => (
    <>
      <DialogHeader>
        <DialogTitle>
          {intent === "found_tenant"
            ? "Congrats! Where did you find your tenant?"
            : intent === "pause"
              ? "Pausing this listing: what changed?"
              : "Before you delete, what changed?"}
        </DialogTitle>
        <DialogDescription>One quick question so we can help. It takes five seconds.</DialogDescription>
      </DialogHeader>
      <RadioGroup
        value={state.reason ?? ""}
        onValueChange={(v) => dispatch({ type: "SET", patch: { reason: v as DelistReason } })}
        className="space-y-2 my-1"
      >
        {reasons.map((r) => (
          <Label
            key={r.code}
            htmlFor={`delist-${r.code}`}
            className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <RadioGroupItem id={`delist-${r.code}`} value={r.code} className="mt-0.5" />
            <span className="text-[14px] font-normal leading-snug">
              {r.label(tenant.siteName)}
              {r.hint && <span className="block text-[12px] text-muted-foreground">{r.hint}</span>}
            </span>
          </Label>
        ))}
      </RadioGroup>

      {state.reason === "found_elsewhere" && (
        <div className="space-y-2">
          <p className="text-[13px] font-medium">Where did you find them?</p>
          <RadioGroup
            value={state.foundVia ?? ""}
            onValueChange={(v) => dispatch({ type: "SET", patch: { foundVia: v as FoundVia } })}
            className="grid grid-cols-1 sm:grid-cols-2 gap-1.5"
          >
            {FOUND_VIA_OPTIONS.map((o) => (
              <Label key={o.code} htmlFor={`via-${o.code}`} className="flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-[13px] font-normal">
                <RadioGroupItem id={`via-${o.code}`} value={o.code} />
                {o.label}
              </Label>
            ))}
          </RadioGroup>
        </div>
      )}

      {state.reason === "found_here" && state.candidates.length > 0 && (
        <div className="space-y-2">
          <p className="text-[13px] font-medium">Which renter? (optional)</p>
          <RadioGroup
            value={state.matchedRequestId ?? ""}
            onValueChange={(v) => dispatch({ type: "SET", patch: { matchedRequestId: v } })}
            className="grid grid-cols-1 gap-1.5"
          >
            {state.candidates.map((c) => (
              <Label key={c.id} htmlFor={`cand-${c.id}`} className="flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-[13px] font-normal">
                <RadioGroupItem id={`cand-${c.id}`} value={c.id} />
                {c.name}
              </Label>
            ))}
          </RadioGroup>
        </div>
      )}

      {(state.reason === "other" || state.reason === "few_requests") && (
        <Textarea
          value={state.reasonText}
          onChange={(e) => dispatch({ type: "SET", patch: { reasonText: e.target.value } })}
          placeholder={state.reason === "other" ? "Optional, what's going on?" : "Optional, anything we should know?"}
          rows={3}
          className="text-[14px]"
        />
      )}

      <DialogFooter className="pt-1">
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={state.submitting}>
          Cancel
        </Button>
        <Button onClick={handlePickReason} disabled={!state.reason || state.submitting}>
          {state.submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Continue
        </Button>
      </DialogFooter>
    </>
  );

  const deleteAnyway = (
    <button
      type="button"
      className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-destructive"
      onClick={() => dispatch({ type: "GO", step: "confirm_delete" })}
      disabled={state.submitting}
    >
      Delete the listing anyway
    </button>
  );

  const renderOffer = () => {
    if (alternative === "keep_published") {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Congrats! When will it be free again?</DialogTitle>
            <DialogDescription>
              Keep {title} published with the new date: renters searching for later months keep finding it, and
              you never start over. Anyone waiting on dates that no longer fit is told automatically.
            </DialogDescription>
          </DialogHeader>
          <DateField
            mode="single"
            value={state.availableAgain}
            onChange={(d) => dispatch({ type: "SET", patch: { availableAgain: d } })}
            placeholder="Free again from…"
            title="Free again from"
            disabled={{ before: today }}
            fromMonth={today}
          />
          {state.error && <ErrorLine text={state.error} />}
          <div className="flex flex-col gap-2 pt-1">
            <Button onClick={handleKeepPublished} disabled={!state.availableAgain || state.submitting}>
              {state.submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {state.availableAgain ? `Keep listed from ${fmt(state.availableAgain)}` : "Keep listed from that date"}
            </Button>
            <Button variant="outline" onClick={handleRented} disabled={state.submitting}>
              Not sure yet, mark as rented
            </Button>
            <div className="flex justify-between items-center gap-3 pt-1">
              <span className="text-[12px] text-muted-foreground">
                {state.pendingCount > 0
                  ? `Marking it rented closes ${state.pendingCount === 1 ? "the open request" : `all ${state.pendingCount} open requests`}; renters are told automatically.`
                  : "Rented listings are hidden until you republish them."}
              </span>
              {deleteAnyway}
            </div>
          </div>
        </>
      );
    }
    if (alternative === "review") {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Let us take a look first</DialogTitle>
            <DialogDescription>
              Listings with more photos, a clear description and up-to-date availability get the most requests.
              Ask the {tenant.siteName} team for a quick, free review. We reply in your inbox.
            </DialogDescription>
          </DialogHeader>
          {state.error && <ErrorLine text={state.error} />}
          <div className="flex flex-col gap-2 pt-1">
            <Button onClick={handleReview} disabled={state.submitting}>
              {state.submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Ask the team for a review
            </Button>
            <Button variant="outline" onClick={handlePause} disabled={state.submitting}>
              Pause the listing for now
            </Button>
            <div className="flex justify-end pt-1">{deleteAnyway}</div>
          </div>
        </>
      );
    }
    // pause (not_renting_now, other)
    return (
      <>
        <DialogHeader>
          <DialogTitle>Pause instead of deleting</DialogTitle>
          <DialogDescription>
            Pausing hides {title} from renters and closes open requests, but keeps your photos, description and
            calendar. Republishing is one tap whenever you're ready.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <p className="text-[13px] font-medium">Remind me to relist on (optional)</p>
          <DateField
            mode="single"
            value={state.reminderDate}
            onChange={(d) => dispatch({ type: "SET", patch: { reminderDate: d } })}
            placeholder="Pick a date"
            title="Remind me on"
            disabled={{ before: today }}
            fromMonth={today}
          />
        </div>
        {state.error && <ErrorLine text={state.error} />}
        <div className="flex flex-col gap-2 pt-1">
          <Button onClick={handlePause} disabled={state.submitting}>
            {state.submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isPublished(listing.status) ? "Pause the listing" : "Keep it paused"}
          </Button>
          <div className="flex justify-end pt-1">{deleteAnyway}</div>
        </div>
      </>
    );
  };

  const renderConfirmDelete = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" /> Delete this listing?
        </DialogTitle>
        <DialogDescription>
          This permanently deletes <span className="font-medium text-foreground">“{title}”</span> with its photos,
          availability and request history. This can’t be undone.
          {state.pendingCount > 0 && (
            <>
              {" "}
              {state.pendingCount === 1 ? "One renter is waiting for an answer" : `${state.pendingCount} renters are waiting for an answer`}
              ; they will be told the home is no longer available.
            </>
          )}
        </DialogDescription>
      </DialogHeader>
      {alternative && alternative !== "delete" && (
        <p className="text-[13px] text-muted-foreground">
          Changed your mind?{" "}
          <button type="button" className="underline underline-offset-2" onClick={() => dispatch({ type: "GO", step: "offer" })}>
            Keep or pause it instead
          </button>
          .
        </p>
      )}
      <div className="space-y-2">
        <label htmlFor="delist-confirm-input" className="text-sm text-muted-foreground">
          Type <span className="font-semibold text-foreground">delete</span> to confirm.
        </label>
        <Input
          id="delist-confirm-input"
          value={state.typed}
          onChange={(e) => dispatch({ type: "SET", patch: { typed: e.target.value } })}
          placeholder="delete"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleDelete();
          }}
        />
      </div>
      {state.error && <ErrorLine text={state.error} />}
      <DialogFooter className="pt-1">
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={state.submitting}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={handleDelete} disabled={!canDelete}>
          {state.submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Delete listing
        </Button>
      </DialogFooter>
    </>
  );

  const renderDone = () => {
    const o = state.outcome!;
    const heading =
      o.action === "deleted" ? "Deleted" : o.action === "paused" ? "Paused" : o.action === "rented" ? "Marked as rented" : "Saved!";
    const body =
      o.action === "deleted"
        ? "The listing is gone. Renters who were waiting have been told."
        : o.action === "paused"
          ? o.reminderDate
            ? `Hidden from renters. We'll remind you on ${fmt(new Date(`${o.reminderDate}T00:00:00`))}.`
            : "Hidden from renters. Republish whenever you're ready."
          : o.action === "rented"
            ? "Hidden from renters. Republish it from your dashboard when it's free again."
            : o.reviewRequested
              ? "The team will reply in your inbox. Your listing stays published."
              : o.availableFrom
                ? `Your listing stays published and shows as available from ${fmt(new Date(`${o.availableFrom}T00:00:00`))}.`
                : "Your listing stays published.";
    return (
      <DialogHeader className="items-center text-center py-4 space-y-2">
        <CheckCircle2 className="h-10 w-10 text-primary mx-auto" aria-hidden="true" />
        <DialogTitle className="font-serif text-xl">{heading}</DialogTitle>
        <DialogDescription className="text-[14px]">{body}</DialogDescription>
      </DialogHeader>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!state.submitting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md max-h-[92svh] overflow-y-auto">
        {state.step === "reason" && renderReason()}
        {state.step === "offer" && renderOffer()}
        {state.step === "confirm_delete" && renderConfirmDelete()}
        {state.step === "done" && renderDone()}
      </DialogContent>
    </Dialog>
  );
};

const ErrorLine = ({ text }: { text: string }) => (
  <p className="text-[13px] text-destructive flex items-center gap-1.5">
    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {text}
  </p>
);

export default DelistDialog;
